/**
 * Sort Schemas for Server-Side Sorting
 *
 * Zod schemas shared between frontend and backend for type-safe sorting.
 */

import { z } from "zod";

// ============================================================================
// Sort Order
// ============================================================================

export const sortOrderSchema = z.enum(["asc", "desc"]);

export type SortOrder = z.infer<typeof sortOrderSchema>;

// ============================================================================
// Sort Config Schema
// ============================================================================

/**
 * Configuration for a single sort column
 */
export const sortConfigSchema = z.object({
  /**
   * Field to sort by. Can be:
   * - Standard field: "startedAt", "duration", "status", etc.
   * - Insight field: "insight-{insightId}" or "insight-{insightName}"
   * - Nested field: "participant.name", "study.name", etc.
   */
  field: z.string(),

  /**
   * Sort direction: ascending or descending
   */
  order: sortOrderSchema,
});

export type SortConfig = z.infer<typeof sortConfigSchema>;

// ============================================================================
// Multi-Column Sort Schema
// ============================================================================

/**
 * Array of sort configs for multi-column sorting
 * First element is primary sort, second is secondary, etc.
 */
export const multiSortConfigSchema = z.array(sortConfigSchema);

export type MultiSortConfig = z.infer<typeof multiSortConfigSchema>;

// ============================================================================
// Interview Sort Fields
// ============================================================================

/**
 * Standard sortable fields for interviews table
 */
export const interviewSortFieldSchema = z.enum([
  "startedAt",
  "endedAt",
  "duration",
  "status",
  "deviceType",
  "browserName",
  "osName",
  "city",
  "country",
]);

export type InterviewSortField = z.infer<typeof interviewSortFieldSchema>;

// ============================================================================
// Default Sort Configuration
// ============================================================================

/**
 * Default sort for interviews: newest first
 */
export const DEFAULT_INTERVIEW_SORT: SortConfig = {
  field: "startedAt",
  order: "desc",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a sort field is an insight field
 */
export function isInsightSortField(field: string): boolean {
  return field.startsWith("insight-");
}

/**
 * Extract insight ID from an insight sort field
 */
export function extractInsightIdFromSortField(field: string): string | null {
  if (!isInsightSortField(field)) {
    return null;
  }
  return field.replace("insight-", "");
}

/**
 * Create an insight sort field from an insight ID
 */
export function createInsightSortField(insightId: string): string {
  return `insight-${insightId}`;
}

/**
 * Convert TanStack Table sorting state to SortConfig array
 */
export function tanstackSortingToSortConfig(
  sorting: { id: string; desc: boolean }[],
): MultiSortConfig {
  return sorting.map((sort) => ({
    field: sort.id,
    order: sort.desc ? "desc" : "asc",
  }));
}

/**
 * Convert SortConfig array to TanStack Table sorting state
 */
export function sortConfigToTanstackSorting(
  sortConfig: MultiSortConfig,
): { id: string; desc: boolean }[] {
  return sortConfig.map((sort) => ({
    id: sort.field,
    desc: sort.order === "desc",
  }));
}
